import Services from '../../mixins/Services';

export default {
	mixins: [Services],
	data() {
		return {
			factoids: [],
			factoidLoading: false,
			factoidError: null,
			sources: [],
			sourcesLoading: false,
			sourcesError: null,
			employers: [],
			employersLoading: false,
			employersError: null,
			customers: [],
			customersLoading: false,
			customersError: null,
			categories: [],
			categoriesLoading: false,
			categoriesError: null,
			guides: [],
			guidesLoading: false,
			guidesError: null,
			faqs: [],
			faqLoading: false,
			faqError: null
		}
	},
	methods: {
		/**
		 * Simplified GET method for admin endpoints (no caching)
		 * @param {string} url - API endpoint URL
		 * @param {Function} onSuccess - Callback function called with response data
		 */
		_GET: function (url, onSuccess) {
			url = url.replace(/\/$/, ''); // Remove trailing slashes
			if (!url.includes(this.api)) {
				url = this.api + url;
			}
			this.$http.get(url)
				.then((result) => {
					if (onSuccess) {
						onSuccess(result.data);
					}
				})
				.catch((result) => {
					if (result.response && result.response.status == 401) {
						this._refreshLogin(() => {
							this._GET(url, onSuccess);
						});
					}
				});
		},

		_DELETE: function (url, onSuccess) {
			url = url.replace(/\/$/, ''); // Remove trailing slashes
			if (!url.includes(this.api)) {
				url = this.api + url;
			}
			this.$http.delete(url)
				.then((result) => {
					if (onSuccess) {
						onSuccess(result.data);
					}
				})
				.catch((result) => {
					if (result.response && result.response.status == 401) {
						this._refreshLogin(() => {
							this._DELETE(url, onSuccess);
						});
					}
				});
		},

		/**
		 * Fetch all factoids from the API
		 * @param {Function} onSuccess - Callback function called with factoids data
		 */
		_getFactoids: function (onSuccess = null) {
			this.factoidLoading = true;
			this.factoidError = null;

			this._GET("/Factoids", (result) => {
				this.factoids = result;
				this.factoidLoading = false;
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Create a new factoid
		 * @param {Object} factoidData - Factoid data object
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_createFactoid: function (factoidData, onSuccess = null) {
			this.$http.post(this.api + '/Factoids', factoidData)
				.then((response) => {
					// Refresh the factoids list
					this._getFactoids();
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error creating factoid:', error);
					this.factoidError = 'Failed to create factoid';
				});
		},

		/**
		 * Update an existing factoid
		 * @param {string} id - Factoid ID
		 * @param {Object} factoidData - Updated factoid data
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_updateFactoid: function (id, factoidData, onSuccess = null) {
			this.$http.put(this.api + '/Factoids/' + id, factoidData)
				.then((response) => {
					// Refresh the factoids list
					this._getFactoids();
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error updating factoid:', error);
					this.factoidError = 'Failed to update factoid';
				});
		},

		/**
		 * Delete a factoid
		 * @param {string} id - Factoid ID
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_deleteFactoid: function (id, onSuccess = null) {
			this.$http.delete(this.api + '/Factoids/' + id)
				.then((response) => {
					// Refresh the factoids list
					this._getFactoids();
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error deleting factoid:', error);
					this.factoidError = 'Failed to delete factoid';
				});
		},

		/**
		 * Fetch all sources from the API
		 * @param {Function} onSuccess - Callback function called with sources data
		 */
		_getSources: function (onSuccess = null) {
			this.sourcesLoading = true;
			this.sourcesError = null;

			this._GET("/Sources", (result) => {
				this.sources = result;
				this.sourcesLoading = false;
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Create a new source
		 * @param {Object} sourceData - Source data object
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_createSource: function (sourceData, onSuccess = null) {
			this.$http.post(this.api + '/Sources', sourceData)
				.then((response) => {
					// Refresh the sources list
					this._getSources();
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error creating source:', error);
					this.sourcesError = 'Failed to create source';
				});
		},

		/**
		 * Update an existing source
		 * @param {string} id - Source ID
		 * @param {Object} sourceData - Updated source data
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_updateSource: function (id, sourceData, onSuccess = null) {
			this.$http.put(this.api + '/Sources/' + id, sourceData)
				.then((response) => {
					// Refresh the sources list
					this._getSources();
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error updating source:', error);
					this.sourcesError = 'Failed to update source';
				});
		},

		/**
		 * Delete a source
		 * @param {string} id - Source ID
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_deleteSource: function (id, onSuccess = null) {
			this.$http.delete(this.api + '/Sources/' + id)
				.then((response) => {
					// Refresh the sources list
					this._getSources();
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error deleting source:', error);
					this.sourcesError = 'Failed to delete source';
				});
		},

		/**
		 * Test a source by fetching page content
		 * @param {Object} sourceData - Source data object with pageUrl, acceptCookiesButton, contentSelector
		 * @param {Function} onSuccess - Callback function called with response data
		 */
		_testSource: function (sourceData, onSuccess = null, onError = null) {
			const params = new URLSearchParams({
				pageUrl: sourceData.pageUrl,
				acceptCookiesButton: sourceData.acceptCookiesButton || '',
				contentSelector: sourceData.contentSelector
			});

			this.$http.get(this.api + '/Page?' + params.toString())
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error testing source:', error);
					if (onError) {
						onError(error.message || 'Failed to fetch page content');
					}
				});
		},

		/**
		 * Fetch all employers from the API
		 * @param {Function} onSuccess - Callback function called with employers data
		 */
		_getEmployers: function (onSuccess = null) {
			this.employersLoading = true;
			this.employersError = null;

			this._GET("/Employers", (result) => {
				this.employers = result;
				this.employersLoading = false;
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Fetch detailed employer information
		 * @param {string} id - Employer ID
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_getEmployerDetails: function (id, onSuccess = null) {
			this._GET("/Employers/" + id, (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Fetch free licenses for an employer
		 * @param {string} id - Employer ID
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_getFreeLicenses: function (id, onSuccess = null) {
			this._GET("/Employers/" + id + "/FreeLicenses", (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Create a new employer
		 * @param {Object} employerData - New employer data
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_createEmployer: function (employerData, onSuccess = null, onError = null) {
			this.$http.post(this.api + '/Employers', employerData)
				.then((response) => {
					// Refresh the employers list
					this._getEmployers();
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error creating employer:', error);
					const errorMsg = 'Failed to create employer';
					this.employersError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Update an existing employer
		 * @param {string} id - Employer ID
		 * @param {Object} employerData - Updated employer data
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_updateEmployer: function (id, employerData, onSuccess = null, onError = null) {
			this.$http.put(this.api + '/Employers/' + id, employerData)
				.then((response) => {
					// Refresh the employers list
					this._getEmployers();
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error updating employer:', error);
					const errorMsg = 'Failed to update employer';
					this.employersError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Upload keys for an employer
		 * @param {string} id - Employer ID
		 * @param {Array} keys - Array of keys to upload
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_uploadKeys: function (id, keys, onSuccess = null, onError = null) {
			const payload = {
				employerId: id,
				keys: keys
			};
			this.$http.post(this.api + '/Employers/' + id + '/CreateEmployerGatewayKeysRequests', payload)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error uploading keys:', error);
					const errorMsg = 'Failed to upload keys';
					this.employersError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Remove keys for an employer
		 * @param {string} id - Employer ID
		 * @param {Array} keys - Array of keys to remove (can be empty to remove all)
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_removeKeys: function (id, keys = [], onSuccess = null, onError = null) {
			const payload = {
				employerId: id,
				keys: keys
			};
			this.$http.post(this.api + '/Employers/' + id + '/DeleteEmployerGatewayKeysRequests', payload)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error removing keys:', error);
					const errorMsg = 'Failed to remove keys';
					this.employersError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Delete an employer
		 * @param {string} id - Employer ID
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_deleteEmployer: function (id, onSuccess = null, onError = null) {
			this.$http.delete(this.api + '/Employers/' + id)
				.then((response) => {
					// Refresh the employers list
					this._getEmployers();
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error deleting employer:', error);
					const errorMsg = 'Failed to delete employer';
					this.employersError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Fetch all customers from the API
		 * @param {Function} onSuccess - Callback function called with customers data
		 */
		_getCustomers: function (onSuccess = null) {
			this.customersLoading = true;
			this.customersError = null;

			this._GET("/Customers", (result) => {
				this.customers = result;
				this.customersLoading = false;
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Fetch current user (self) information
		 * @param {Function} onSuccess - Callback function called on success
		 */
		getSelf: function (onSuccess = null) {
			this._GET("/Customers/00000000-0000-0000-0000-000000000000", (result) => {
				if (result && result.id) {
					this.selfId = result.id;
				}
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Fetch customer credit transactions
		 * @param {string} id - Customer ID
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_getCustomerCreditTransactions: function (id, onSuccess = null) {
			this._GET("/Customers/" + id + "/Discounts/CustomerCreditTransactions", (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Add credit to customer account
		 * @param {string} customerId - Customer ID
		 * @param {number} amount - Amount of credit to add
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_addCustomerCredit: function (customerId, amount, onSuccess = null, onError = null) {
			const payload = {
				customerID: customerId,
				amount: amount
			};
			this.$http.post(this.api + '/Discounts/CustomerCreditTransactions', payload)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error adding credit:', error);
					const errorMsg = 'Failed to add credit';
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Post customer fact card data
		 * @param {string} customerId - Customer ID
		 * @param {Object} facts - Facts object to post
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_postFactCard: function (customerId, facts, onSuccess = null, onError = null) {
			const payload = [];
			for (const key in facts) {
				payload.push({ name: key, value: facts[key] });
			}
			this.$http.post(this.api + '/Customers/' + customerId + '/FactCard', payload)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error posting fact card:', error);
					const errorMsg = 'Failed to post fact card';
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Generate moves for a customer
		 * @param {string} customerId - Customer ID
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_generateMoves: function (customerId, onSuccess = null, onError = null) {
			this.$http.post(this.api + '/Moves/GenerationRequests?customerId=' + customerId, null)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error generating moves:', error);
					const errorMsg = 'Failed to generate moves';
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Fetch detailed customer information
		 * @param {string} id - Customer ID
		 * @param {boolean} getFacts - Whether to include facts in the response
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_getCustomerDetails: function (id, getFacts = false, onSuccess = null) {
			this._GET("/Customers/" + id + "?getEmployer=true&getFacts=" + getFacts, (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Fetch customer policies (insurance)
		 * @param {string} id - Customer ID
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_getCustomerPolicies: function (id, onSuccess = null) {
			this._GET("/Customers/" + id + "/Policies", (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Fetch customer investment accounts
		 * @param {string} id - Customer ID
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_getCustomerInvestments: function (id, onSuccess = null) {
			this._GET("/Customers/" + id + "/InvestmentHubAccounts", (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Fetch customer moves
		 * @param {string} id - Customer ID
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_getCustomerMoves: function (id, onSuccess = null) {
			this._GET("/Customers/" + id + "/Moves", (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Fetch customer completed moves
		 * @param {string} id - Customer ID
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_getCustomerCompletedMoves: function (id, onSuccess = null) {
			this._GET("/Customers/" + id + "/Moves?filterByActioned=true", (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Link or unlink an employer to a customer
		 * @param {string} customerId - Customer ID
		 * @param {string} employerId - Employer ID (null to unlink)
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_linkEmployerToCustomer: function (customerId, employerId, onSuccess = null, onError = null) {
			if (employerId) {
				// Link employer
				this.$http.post(this.api + '/Customers/' + customerId + '/LinkToEmployerRequests?employerId=' + employerId, null)
					.then((response) => {
						this._getCustomers();
						if (onSuccess) {
							onSuccess(response.data);
						}
					})
					.catch((error) => {
						console.error('Error linking employer:', error);
						const errorMsg = 'Failed to link employer';
						this.customersError = errorMsg;
						if (onError) {
							onError(errorMsg);
						}
					});
			} else {
				// Unlink employer
				this.$http.post(this.api + '/Customers/' + customerId + '/UnlinkFromEmployerRequests', null)
					.then((response) => {
						this._getCustomers();
						if (onSuccess) {
							onSuccess(response.data);
						}
					})
					.catch((error) => {
						console.error('Error unlinking employer:', error);
						const errorMsg = 'Failed to unlink employer';
						this.customersError = errorMsg;
						if (onError) {
							onError(errorMsg);
						}
					});
			}
		},

		/**
		 * Fetch all customer documents (uploads)
		 * @param {Function} onSuccess - Callback function called with documents data
		 */
		_getDocuments: function (onSuccess = null) {
			this._GET("/Documents", (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Retry document processing
		 * @param {string} id - Document ID
		 * @param {string} flatObject - Optional base64-encoded flat object parameter
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_retryDocument: function (id, flatObject = null, onSuccess = null, onError = null) {
			const url = flatObject
				? this.api + '/Documents/' + id + '/RetryRequests?flatObject=' + btoa(flatObject)
				: this.api + '/Documents/' + id + '/RetryRequests';

			this.$http.post(url, null)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error retrying document:', error);
					const errorMsg = 'Failed to retry document processing';
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Fetch all CMS files
		 * @param {Function} onSuccess - Callback function called with files data
		 * @param {Function} onError - Callback function called on error
		 */
		_getFiles: function (onSuccess = null, onError = null) {
			this.$http.get(this.api + '/CMS/Files?ordered=true')
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error fetching files:', error);
					const errorMsg = 'Failed to load file list';
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Upload a file chunk
		 * @param {string} base64Content - Base64-encoded file chunk content
		 * @param {string} fileName - Name of the file
		 * @param {number} chunkIndex - Index of the chunk
		 * @param {number} totalChunks - Total number of chunks
		 * @param {string} uploadId - Unique upload ID
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_uploadFileChunk: function (base64Content, fileName, chunkIndex, totalChunks, uploadId, onSuccess = null, onError = null) {
			const payload = {
				content: base64Content,
				fileName: fileName,
				chunkIndex: chunkIndex,
				totalChunks: totalChunks,
				UploadId: uploadId
			};

			this.$http.post(this.api + '/CMS/Files/UploadRequests', payload)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error uploading file chunk:', error);
					const errorMsg = 'Failed to upload file chunk';
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Delete a file from CMS
		 * @param {string} fileName - Name of the file to delete
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_deleteFile: function (fileName, onSuccess = null, onError = null) {
			const url = this.api + '/CMS/Files/' + fileName + '?file=' + fileName;

			this.$http.delete(url)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error deleting file:', error);
					const errorMsg = 'Failed to delete file';
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Fetch all categories from the API
		 * @param {Function} onSuccess - Callback function called with categories data
		 */
		_getCategories: function (onSuccess = null) {
			this.categoriesLoading = true;
			this.categoriesError = null;

			this._GET("/Discounts/Categories", (result) => {
				this.categories = result;
				this.categoriesLoading = false;
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Create a new category
		 * @param {Object} categoryData - Category data object
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_createCategory: function (categoryData, onSuccess = null, onError = null) {
			this.$http.post(this.api + '/Discounts/Categories', categoryData)
				.then((response) => {
					// Refresh the categories list
					this._getCategories();
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error creating category:', error);
					const errorMsg = 'Failed to create category';
					this.categoriesError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Update an existing category
		 * @param {string} id - Category ID
		 * @param {Object} categoryData - Updated category data
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_updateCategory: function (id, categoryData, onSuccess = null, onError = null) {
			this.$http.put(this.api + '/Discounts/Categories/' + id, categoryData)
				.then((response) => {
					// Refresh the categories list
					this._getCategories();
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error updating category:', error);
					const errorMsg = 'Failed to update category';
					this.categoriesError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Delete a category
		 * @param {string} id - Category ID
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_deleteCategory: function (id, onSuccess = null, onError = null) {
			this.$http.delete(this.api + '/Discounts/Categories/' + id)
				.then((response) => {
					// Refresh the categories list
					this._getCategories();
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error deleting category:', error);
					const errorMsg = 'Failed to delete category';
					this.categoriesError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Fetch all guides from the API
		 * @param {Function} onSuccess - Callback function called with guides data
		 */
		_getGuides: function (onSuccess = null) {
			this.guidesLoading = true;
			this.guidesError = null;

			this._GET("/Plans", (result) => {
				this.guides = result;
				this.guidesLoading = false;
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Create a new guide
		 * @param {Object} guideData - Guide data object
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_createGuide: function (guideData, onSuccess = null, onError = null) {
			this.$http.post(this.api + '/Plans', guideData)
				.then((response) => {
					this._getGuides(); // Refresh list
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error creating guide:', error);
					const errorMsg = 'Failed to create guide';
					this.guidesError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Update an existing guide
		 * @param {string} id - Guide ID
		 * @param {Object} guideData - Updated guide data
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_updateGuide: function (id, guideData, onSuccess = null, onError = null) {
			this.$http.put(this.api + '/Plans/' + id, guideData)
				.then((response) => {
					this._getGuides(); // Refresh list
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error updating guide:', error);
					const errorMsg = 'Failed to update guide';
					this.guidesError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Delete a guide
		 * @param {string} id - Guide ID
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_deleteGuide: function (id, onSuccess = null, onError = null) {
			this.$http.delete(this.api + '/Plans/' + id)
				.then((response) => {
					this._getGuides(); // Refresh list
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error deleting guide:', error);
					const errorMsg = 'Failed to delete guide';
					this.guidesError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Upload a category image file
		 * @param {Object} fileData - File data with content and fileName
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_uploadCategoryImage: function (fileData, onSuccess = null, onError = null) {
			this.$http.post(this.api + '/CMS/Files/UploadRequests', fileData)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error uploading category image:', error);
					const errorMsg = 'Failed to upload image';
					this.categoriesError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Upload a file to employer knowledge base
		 * @param {string} employerId - Employer ID
		 * @param {Object} fileData - File data with content and fileName
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_uploadEmployerKnowledgeBase: function (employerId, fileData, onSuccess = null, onError = null) {
			this.$http.post(this.api + `/Employers/${employerId}/KnowledgeBase/Files/UploadRequests`, fileData)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error uploading employer knowledge base file:', error);
					const errorMsg = 'Failed to upload file';
					this.employersError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Fetch all files from employer knowledge base
		 * @param {string} employerId - Employer ID
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_getEmployerKnowledgeBaseFiles: function (employerId, onSuccess = null, onError = null) {
			this._GET(this.api + `/Employers/${employerId}/KnowledgeBase/Files`, onSuccess, onError);
		},

		_deleteEmployerKnowledgeBaseFile: function (employerId, fileId, onSuccess = null, onError = null) {
			this._DELETE(this.api + `/Employers/${employerId}/KnowledgeBase/Files/${fileId}`, onSuccess, onError);
		},

		/**
		 * Fetch all vouchers from the API
		 * @param {Function} onSuccess - Callback function called with vouchers data
		 */
		_getVouchers: function (onSuccess = null) {
			this._GET("/Discounts?all=true", (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Fetch all merchants from the API
		 * @param {Function} onSuccess - Callback function called with merchants data
		 */
		_getMerchants: function (onSuccess = null) {
			this._GET("/Merchants", (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Update an existing voucher
		 * @param {string} id - Voucher ID
		 * @param {Object} voucherData - Updated voucher data
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_updateVoucher: function (id, voucherData, onSuccess = null, onError = null) {
			this.$http.put(this.api + '/Discounts/' + id, voucherData)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error updating voucher:', error);
					const errorMsg = 'Failed to update voucher';
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Fetch discount summary report
		 * @param {Function} onSuccess - Callback function called with report data
		 */
		_getDiscountReport: function (onSuccess = null) {
			this._GET("/Discounts/Reports/Summary", (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Fetch payment summary report
		 * @param {Function} onSuccess - Callback function called with report data
		 */
		_getPaymentReport: function (onSuccess = null) {
			this._GET("/Payments/Reports/Summary", (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Fetch pre-load instructions for chat configuration
		 * @param {Function} onSuccess - Callback function called with preloads data
		 */
		_getPreLoadInstructions: function (onSuccess = null) {
			this._GET("/Chats/PreLoadInstructions", (result) => {
				if (onSuccess) {
					onSuccess(result || []);
				}
			});
		},

		/**
		 * Create a new pre-load instruction
		 * @param {Object} data - Pre-load instruction data
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_createPreLoadInstruction: function (data, onSuccess = null, onError = null) {
			this.$http.post(this.api + '/Chats/PreLoadInstructions', data)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error creating pre-load instruction:', error);
					if (onError) {
						onError('Failed to create pre-load instruction');
					}
				});
		},

		/**
		 * Update an existing pre-load instruction
		 * @param {string} id - Pre-load instruction ID
		 * @param {Object} data - Updated pre-load instruction data
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_updatePreLoadInstruction: function (id, data, onSuccess = null, onError = null) {
			this.$http.put(this.api + '/Chats/PreLoadInstructions/' + id, { id: id, ...data })
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error updating pre-load instruction:', error);
					if (onError) {
						onError('Failed to update pre-load instruction');
					}
				});
		},

		/**
		 * Delete a pre-load instruction
		 * @param {string} id - Pre-load instruction ID
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_deletePreLoadInstruction: function (id, onSuccess = null, onError = null) {
			this.$http.delete(this.api + '/Chats/PreLoadInstructions/' + id)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error deleting pre-load instruction:', error);
					if (onError) {
						onError('Failed to delete pre-load instruction');
					}
				});
		},

		/**
		 * Fetch context app actions (context hints) for chat configuration
		 * @param {Function} onSuccess - Callback function called with contexts data
		 */
		_getContextAppActions: function (onSuccess = null) {
			this._GET("/Chats/ContextAppActions", (result) => {
				if (onSuccess) {
					onSuccess(result || []);
				}
			});
		},

		/**
		 * Create a new context app action
		 * @param {Object} data - Context app action data
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_createContextAppAction: function (data, onSuccess = null, onError = null) {
			this.$http.post(this.api + '/Chats/ContextAppActions', data)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error creating context app action:', error);
					if (onError) {
						onError('Failed to create context hint');
					}
				});
		},

		/**
		 * Update an existing context app action
		 * @param {string} id - Context app action ID
		 * @param {Object} data - Updated context app action data
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_updateContextAppAction: function (id, data, onSuccess = null, onError = null) {
			this.$http.put(this.api + '/Chats/ContextAppActions/' + id, { id: id, ...data })
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error updating context app action:', error);
					if (onError) {
						onError('Failed to update context hint');
					}
				});
		},

		/**
		 * Delete a context app action
		 * @param {string} id - Context app action ID
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_deleteContextAppAction: function (id, onSuccess = null, onError = null) {
			this.$http.delete(this.api + '/Chats/ContextAppActions/' + id)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error deleting context app action:', error);
					if (onError) {
						onError('Failed to delete context hint');
					}
				});
		},

		/**
		 * Send a test chat message
		 * @param {string} customerId - Customer ID
		 * @param {Object} message - Message object with body and optional providerOverride
		 * @param {Function} onSuccess - Callback function called with response
		 * @param {Function} onError - Callback function called on error
		 */
		_sendChatMessage: function (customerId, message, onSuccess = null, onError = null) {
			this.$http.post(this.api + '/Chats/ChatRequests?customerId=' + customerId, {
				...message,
				customerId: customerId
			})
			.then((response) => {
				if (onSuccess) {
					onSuccess(response.data);
				}
			})
			.catch((error) => {
				console.error('Error sending chat message:', error);
				if (onError) {
					onError(error.message || 'Failed to send message');
				}
			});
		},

		/**
		 * Fetch customer notes
		 * @param {string} customerId - Customer ID
		 * @param {Function} onSuccess - Callback function called with notes data
		 */
		_getCustomerNotes: function (customerId, onSuccess = null) {
			this._GET("/Customers/" + customerId + "/Notes", (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Create a new customer note
		 * @param {string} customerId - Customer ID
		 * @param {Object} noteData - Note data object
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_createCustomerNote: function (customerId, noteData, onSuccess = null, onError = null) {
			this.$http.post(this.api + '/Customers/' + customerId + '/Notes', noteData)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error creating customer note:', error);
					const errorMsg = 'Failed to create note';
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Update customer note via update request
		 * @param {string} customerId - Customer ID
		 * @param {Object} updateData - Update data with note field
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_updateCustomerNote: function (customerId, updateData, onSuccess = null, onError = null) {
			this.$http.post(this.api + '/Customers/' + customerId + '/UpdateRequests', updateData)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error updating customer note:', error);
					const errorMsg = 'Failed to update note';
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		// ==================== Cache Management Methods ====================

		/**
		 * Get cache statistics
		 * @param {Function} onSuccess - Callback function called with cache stats
		 */
		_getCacheStats: function (onSuccess = null) {
			this._GET("/Cache/Stats", onSuccess);
		},

		/**
		 * Get cache log entries
		 * @param {Function} onSuccess - Callback function called with log entries
		 */
		_getCacheLog: function (onSuccess = null) {
			this._GET("/Cache/Log", onSuccess);
		},

		/**
		 * Get refresh queue count
		 * @param {Function} onSuccess - Callback function called with queue count
		 */
		_getQueueCount: function (onSuccess = null) {
			this._GET("/Cache/RefreshQueue/Count", onSuccess);
		},

		/**
		 * Get cached URL content
		 * @param {string} url - URL to fetch from cache
		 * @param {Function} onSuccess - Callback function called with cached data
		 */
		_getCachedUrl: function (url, onSuccess = null) {
			this._GET("/Cached/" + url, onSuccess);
		},

		/**
		 * Process cache refresh queue
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_processQueue: function (onSuccess = null) {
			const url = this.api + "/Cache/RefreshQueue/ProcessRequests";
			this.$http.post(url)
				.then((result) => {
					if (onSuccess) {
						onSuccess(result.data);
					}
				})
				.catch((result) => {
					if (result.response && result.response.status == 401) {
						this._refreshLogin(() => {
							this._processQueue(onSuccess);
						});
					}
				});
		},

		/**
		 * Flush a specific URL from cache
		 * @param {string} url - URL to flush
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_flushUrl: function (url, onSuccess = null) {
			const apiUrl = this.api + "/Cached/" + url;
			this.$http.put(apiUrl)
				.then((result) => {
					if (onSuccess) {
						onSuccess(result.data);
					}
				})
				.catch((result) => {
					if (result.response && result.response.status == 401) {
						this._refreshLogin(() => {
							this._flushUrl(url, onSuccess);
						});
					}
				});
		},

		/**
		 * Clear all cache
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_clearCache: function (onSuccess = null) {
			this._DELETE("/Cache", onSuccess);
		},

		/**
		 * Clear cache statistics
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_clearCacheStats: function (onSuccess = null) {
			this._DELETE("/Cache/Stats", onSuccess);
		},

		/**
		 * Delete URL pattern from cache (supports wildcards)
		 * @param {string} url - URL pattern to delete
		 * @param {Function} onSuccess - Callback function called on success
		 */
		_deleteUrl: function (url, onSuccess = null) {
			this._DELETE("/Cached/" + url, onSuccess);
		},

		// ==================== AI Chat Messages Methods ====================

		/**
		 * Fetch AI chat messages with filters
		 * @param {Object} filters - Filter object with customerId, fromDateTime, toDateTime
		 * @param {Function} onSuccess - Callback function called with messages data
		 */
		_getChatMessages: function (filters, onSuccess = null) {
			const params = new URLSearchParams({
				customerId: filters.customerId || '',
				fromDateTime: filters.fromDateTime,
				toDateTime: filters.toDateTime,
			});

			this._GET('/Customers/Chats/Messages?' + params.toString(), (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		// ==================== Walkthrough Methods ====================

		/**
		 * Fetch all walkthroughs from the API
		 * @param {Function} onSuccess - Callback function called with walkthroughs data
		 */
		_getWalkthroughs: function (onSuccess = null) {
			this._GET("/CMS/Walkthroughs", (result) => {
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Create a new walkthrough
		 * @param {Object} walkthroughData - Walkthrough data object
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_createWalkthrough: function (walkthroughData, onSuccess = null, onError = null) {
			this.$http.post(this.api + '/CMS/Walkthroughs', walkthroughData)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error creating walkthrough:', error);
					const errorMsg = 'Failed to create walkthrough';
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Update an existing walkthrough
		 * @param {string} id - Walkthrough ID
		 * @param {Object} walkthroughData - Updated walkthrough data
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_updateWalkthrough: function (id, walkthroughData, onSuccess = null, onError = null) {
			this.$http.put(this.api + '/CMS/Walkthroughs/' + id, walkthroughData)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error updating walkthrough:', error);
					const errorMsg = 'Failed to update walkthrough';
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Delete a walkthrough
		 * @param {string} id - Walkthrough ID
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_deleteWalkthrough: function (id, onSuccess = null, onError = null) {
			this.$http.delete(this.api + '/CMS/Walkthroughs/' + id)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error deleting walkthrough:', error);
					const errorMsg = 'Failed to delete walkthrough';
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		// ==================== FAQ Methods ====================

		/**
		 * Fetch all FAQs from the API
		 * @param {Function} onSuccess - Callback function called with FAQs data
		 */
		_getFaqs: function (onSuccess = null) {
			this.faqLoading = true;
			this.faqError = null;

			this._GET("/Faq", (result) => {
				this.faqs = result;
				this.faqLoading = false;
				if (onSuccess) {
					onSuccess(result);
				}
			});
		},

		/**
		 * Create a new FAQ
		 * @param {Object} faqData - FAQ data object
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_createFaq: function (faqData, onSuccess = null, onError = null) {
			this.$http.post(this.api + '/Faq', faqData)
				.then((response) => {
					// Refresh the FAQs list
					this._getFaqs();
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error creating FAQ:', error);
					const errorMsg = 'Failed to create FAQ';
					this.faqError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Update an existing FAQ
		 * @param {string} id - FAQ ID
		 * @param {Object} faqData - Updated FAQ data
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_updateFaq: function (id, faqData, onSuccess = null, onError = null) {
			this.$http.put(this.api + '/Faq/' + id, faqData)
				.then((response) => {
					// Refresh the FAQs list
					this._getFaqs();
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error updating FAQ:', error);
					const errorMsg = 'Failed to update FAQ';
					this.faqError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		/**
		 * Delete a FAQ
		 * @param {string} id - FAQ ID
		 * @param {Function} onSuccess - Callback function called on success
		 * @param {Function} onError - Callback function called on error
		 */
		_deleteFaq: function (id, onSuccess = null, onError = null) {
			this.$http.delete(this.api + '/Faq/' + id)
				.then((response) => {
					// Refresh the FAQs list
					this._getFaqs();
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error deleting FAQ:', error);
					const errorMsg = 'Failed to delete FAQ';
					this.faqError = errorMsg;
					if (onError) {
						onError(errorMsg);
					}
				});
		},

		// ============ Quizzes ============

		_getQuizzes: function (onSuccess = null, onError = null) {
			this.$http.get(this.api + '/Quizzes?onlyTodaysQuizzes=false&todaysQuestionsOnly=false&tomorrowsQuestionsOnly=false')
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error fetching quizzes:', error);
					if (onError) {
						onError('Failed to fetch quizzes');
					}
				});
		},

		_createQuiz: function (quizData, onSuccess = null, onError = null) {
			this.$http.post(this.api + '/Quizzes', quizData)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error creating quiz:', error);
					if (onError) {
						onError('Failed to create quiz');
					}
				});
		},

		_updateQuiz: function (id, quizData, onSuccess = null, onError = null) {
			this.$http.put(this.api + '/Quizzes/' + id, quizData)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error updating quiz:', error);
					if (onError) {
						onError('Failed to update quiz');
					}
				});
		},

		_deleteQuiz: function (id, onSuccess = null, onError = null) {
			this.$http.delete(this.api + '/Quizzes/' + id)
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error deleting quiz:', error);
					if (onError) {
						onError('Failed to delete quiz');
					}
				});
		},

		_resetQuizDates: function (id, startDate, secsInterval, onSuccess = null, onError = null) {
			const params = new URLSearchParams({
				secsInterval: secsInterval,
				startDate: startDate
			});
			this.$http.post(this.api + '/Quizzes/' + id + '/ResetDateRequests_TEST?' + params.toString())
				.then((response) => {
					if (onSuccess) {
						onSuccess(response.data);
					}
				})
				.catch((error) => {
					console.error('Error resetting quiz dates:', error);
					if (onError) {
						onError('Failed to reset quiz dates');
					}
				});
		}

	}
}
